import React, { useEffect, useContext } from 'react';
import * as Sentry from '@sentry/browser';
import { useHistory } from 'react-router-dom';
import AuthFactory from '../../services/auth';
import AuthService from '../../services/auth/AuthService';
import routes from '../../utils/constants/routes';
import UserProvider from '../../services/entities/UserProvider';
import RocketLoader from '../../components/globals/RocketLoader';
import { preloadAuthenticatedComponents } from '../../routes/authenticated';
import { Layout } from '../../contexts/LayoutContext';
import useCallGA, { constants } from '../../hooks/useCallGA';
import { UserSettingsContext } from '../../contexts/UserSettingsContext';
import { QueryStringContext } from '../../contexts/QueryStringContext';
import { GA4Context } from '../../contexts/GA4Context';
import { HJContexts } from '../../contexts/HJContexts';
import RolesContext from '../../contexts/RolesContext';
import { FACEBOOK, APPLE } from '../../utils/constants/globals';
import { QUERY_KEYS } from '../../utils/constants/settings';
/**
 * @component Callback
 * this component is called inmediately before
 * the user perform authorization with email in the app
 */
const Callback = () => {
  const history = useHistory();
  const callGA = useCallGA();
  const {
    fetchUserSettings,
    setOnboardingDetails,
    getBasedLocationSettings,
  } = useContext(UserSettingsContext);
  const { fetchAndUpdate } = useContext(RolesContext);
  const { setLoginInfo, setSignUpInfo } = useContext(GA4Context);
  const { identifyHJUser, identifyClarityUser } = useContext(HJContexts);
  const { removeQueryStringVal, firstLocation, cleanFirstLocation} = useContext(QueryStringContext);
  const isApp = () => {
    let validateApp = false;
    const userAgentString = navigator.userAgent;
    if (userAgentString.includes('Convertify')) {
      validateApp = true;
      if (userAgentString.includes('ConvertifyIOS')) {
        validateApp = false;
      }
    }
    return validateApp;
  };

  const isAppIOS = () => {
    let validateAppIOS = false;
    const userAgentString = navigator.userAgent;
    if (userAgentString.includes('ConvertifyIOS')) {
      validateAppIOS = true;
    }
    return validateAppIOS;
  };

  const getActionAnalyticsLogin = (type) => {
    const actionAnalytics = { action: `Login with ${type}`, label: '(button)' };
    if (type !== FACEBOOK && type !== APPLE) {
      actionAnalytics.action = 'Login with Email';
    }
    return actionAnalytics;
  };
  const getAuthCallbackInfo = () => {
    const search = window.location?.search;
    const urlParams = new URLSearchParams(search);
    const tempVars = {};
    urlParams.forEach((val, key) => {
      tempVars[key] = val;
    });
    if (tempVars.auth_flow_type) {
      const response = {
        accessToken: tempVars.access_token,
        idToken: tempVars.id_token,
        expiresIn: tempVars.expires_in,
        isAuthPerformed: true,
        authFlowType: tempVars.auth_flow_type,
      };
      return response;
    }
    return tempVars;
  };

  const getCreatUserAnalytics = async (type) => {
    const infoAnalytics = {
      pagePath: '/create-account/form',
      title: 'Create account',
      category: 'Onboarding Step 1',
      action: `Register. Start my free trial (${type})`,
      label: '(button)',
      event: 'action',
    };
    const isAppAndroid = isApp();
    const isAppApple = isAppIOS();

    if (isAppAndroid || isAppApple) {
      infoAnalytics.pagePath = '/login-app';
      infoAnalytics.title = 'Create account via App';
    }

    if (type !== FACEBOOK && type !== APPLE) {
      infoAnalytics.action = 'Register. Start my free trial (email)';
    }
    return infoAnalytics;
  };

  const logOutUser = () => {
    const isAppAndroid = isApp();
    const isAppApple = isAppIOS();
    // TODO: Give feedback about the login problem
    AuthService.unsetAuthData();
    if (isAppAndroid || isAppApple) {
      history.push({
        pathname: routes.LOGIN_APP,
      });
      return;
    }
    history.push({
      pathname: routes.LOGIN,
    });
  };

  const createUser = async (user) => {
    console.log({ userOnCreation: user });
    const responseCreation = await UserProvider.create(user);
    if (responseCreation.success) {
      setOnboardingDetails((details) => ({
        ...details,
        requiredCard: responseCreation.data.requiredCard,
      }));
      const { authId } = responseCreation.data;
      window.localStorage.setItem('authID', authId);
      AuthService.unsetUserData();
      const infoAnalytics = await getCreatUserAnalytics(user.type);
      callGA(infoAnalytics);
      // send a direct event on tag manager
      callGA({
        task: constants.SET,
        taskData: { user_id: AuthService.getAuthID() },
      });
      setSignUpInfo(user);
      setLoginInfo();
      identifyHJUser();
      identifyClarityUser();
      /*
      * Double push necessary so when user clicks back in browser they get
      * redirect to dashboard and not return to callback
      */
      await fetchUserSettings();
      history.push(routes.DASHBOARD);
      if (user.planSelected.name !== "collaborator") {
        history.push({
          pathname: routes.ONBOARDING_LAUNCHER,
          state: {
            from: routes.CREATE_ACCOUNT,
          },
        });
      }
      preloadAuthenticatedComponents();
    }
  };

  useEffect(() => {
    const execute = async () => {
      let userExist = false;
      const authInf = getAuthCallbackInfo();
      const isAndroidFlow = (authInf.authFlowType === 'fbAndroid');
      try {
        const sessionService = await AuthService.authCheckSession(isAndroidFlow);
        const settingLocation = await getBasedLocationSettings();
        // validate the session with auth0
        if (sessionService.session) {
          const extraPayload = isAndroidFlow ? authInf : false;
          let userToken = await AuthService.afterAuth(settingLocation, extraPayload);
          if (!userToken.error) {
            AuthFactory.initAuthAppSync(AuthService.getIdToken());
            const signUpData = AuthService.getUserData();
            const currencyCodeStorage = window.localStorage.getItem('currencyCode');
            const countryCodeStorage = window.localStorage.getItem('countryCode');
            signUpData.isIOS = isAppIOS();
            signUpData.isAndroid = isApp();
            if (signUpData && signUpData.isIOS && currencyCodeStorage && countryCodeStorage) {
              signUpData.currencyCode = currencyCodeStorage;
              signUpData.countryCode = countryCodeStorage;
              signUpData.isOptIn = false;
            }
            if (signUpData) {
              userToken = { ...userToken, ...signUpData };
            }
            AuthService.unsetUserData();
            const responseAuthId = await UserProvider.verifyAuthId();
            const response = await UserProvider.fetchByEmail('Holdermail');
            userExist = responseAuthId.success;
            if (!userExist) { // user not found by authId
              if (!response.success) { // user not found by email
                // user not found, create user
                await createUser(userToken, isAndroidFlow);
                removeQueryStringVal(QUERY_KEYS.tt);
                return;
              } else if (response.data.authId.includes('collab_')) {
                await createUser(userToken, isAndroidFlow);
                removeQueryStringVal(QUERY_KEYS.tt);
                return;
              }
              // SETTING FLAG FOR SHOW MESSAGE USER WHITH EMAIL EXISTS
              window.localStorage.setItem('registederuser', 'true');
              // user found by email, verify if the user have another account
              logOutUser();
              return;
            }
            if (isAndroidFlow && response.data) {
              window.localStorage.setItem('authID', response.data?.authId);
            }
            setLoginInfo();
            identifyHJUser();
            identifyClarityUser();
            removeQueryStringVal(QUERY_KEYS.tt);
            await fetchUserSettings();
            await fetchAndUpdate();

            callGA(getActionAnalyticsLogin(userToken.type));
            // send a direct event on tag manager
            callGA({
              task: constants.SET,
              taskData: { user_id: AuthService.getAuthID() },
            });
            let loginRoute = routes.DASHBOARD;
            if (firstLocation !== 'firstLocation') {
              loginRoute = firstLocation;
              cleanFirstLocation();
            }
            history.push({
              pathname: loginRoute,
              state: {
                from: routes.LOGIN,
              },
            });
            preloadAuthenticatedComponents();
            return;
          }
          logOutUser();
          return;
        }
        logOutUser();
        return;
      } catch (e) {
        Sentry.captureException(e);
        logOutUser();
      }
    };
    execute();
  }, []);

  useEffect(() => {
    window.setSettingPayIOS = async (currencyCode, countryCode) => {
      window.localStorage.setItem('currencyCode', currencyCode);
      window.localStorage.setItem('countryCode', countryCode);
    };
  }, []);

  return (
    <>
      <Layout
        Header={<></>}
        Footer={<></>}
        accessOnTrialEnds
      />

      <RocketLoader />
    </>
  );
};

export default Callback;
